import {
  LINES_PER_CLASS,
  MAX_MESSAGE_BYTES,
  RHYME_CLASS_COUNT,
  WITHIN_CLASS_BITS,
  minimumLineCount,
  payloadBitsForLine,
  rhymeClassOf,
  withinClassIndexOf,
} from "./constants";
import { bitsToBytes, bitsToNumber, bytesToBits, concatBits, numberToBits } from "./bits";
import { formatPoem, parsePoem } from "./format";
import { deriveKey } from "../crypto/sodium";
import { decryptWideBlock, encryptWideBlock } from "../crypto/feistel";
import { AmbiguousCorpusLineError, type Corpus } from "../corpus/types";

const utf8Encoder = new TextEncoder();
const fatalUtf8Decoder = new TextDecoder("utf-8", { fatal: true });

export class CodecError extends Error {
  constructor(
    readonly code:
      | "message-too-long"
      | "line-not-found"
      | "ambiguous-line"
      | "rhyme-mismatch"
      | "invalid-padding"
      | "non-minimal"
      | "wrong-key",
    message: string,
  ) {
    super(message);
    this.name = "CodecError";
  }
}

export function estimateLineCount(message: string): number {
  return minimumLineCount(utf8Encoder.encode(message).length);
}

function packCiphertext(ciphertext: Uint8Array, lineCount: number): Uint8Array {
  const messageBits = bytesToBits(ciphertext);
  const totalCapacity = Array.from({ length: lineCount }, (_, index) => payloadBitsForLine(index + 1)).reduce(
    (sum, value) => sum + value,
    0,
  );
  const packed = new Uint8Array(totalCapacity);
  packed.set(messageBits);
  packed[messageBits.length] = 1;
  return packed;
}

function unpackCiphertext(packed: Uint8Array, lineCount: number): Uint8Array {
  let terminator = packed.length - 1;
  while (terminator >= 0 && packed[terminator] === 0) terminator -= 1;
  if (terminator < 0 || terminator % 8 !== 0) {
    throw new CodecError("invalid-padding", "诗帖的收尾校验不正确。");
  }
  const byteLength = terminator / 8;
  if (minimumLineCount(byteLength) !== lineCount) {
    throw new CodecError("non-minimal", "诗帖行数与消息长度不一致。");
  }
  return bitsToBytes(packed.subarray(0, terminator));
}

export async function encodeMessage(
  message: string,
  username: string,
  secret: string,
  corpus: Corpus,
): Promise<string> {
  const plaintext = utf8Encoder.encode(message);
  if (plaintext.length > MAX_MESSAGE_BYTES) {
    throw new CodecError("message-too-long", `消息不能超过 ${MAX_MESSAGE_BYTES.toLocaleString()} 字节。`);
  }
  await corpus.ensureReady();
  const key = await deriveKey(username, secret, corpus.id);
  return encodeMessageWithKey(message, key, corpus);
}

export async function encodeMessageWithKey(
  message: string,
  key: Uint8Array,
  corpus: Corpus,
): Promise<string> {
  const plaintext = utf8Encoder.encode(message);
  if (plaintext.length > MAX_MESSAGE_BYTES) {
    throw new CodecError("message-too-long", `消息不能超过 ${MAX_MESSAGE_BYTES.toLocaleString()} 字节。`);
  }
  await corpus.ensureReady();
  const ciphertext = await encryptWideBlock(plaintext, key);
  const lineCount = minimumLineCount(ciphertext.length);
  const packed = packCiphertext(ciphertext, lineCount);
  const indices: number[] = [];
  let offset = 0;
  let rhymeClass = 0;

  for (let lineNumber = 1; lineNumber <= lineCount; lineNumber += 1) {
    const width = payloadBitsForLine(lineNumber);
    const value = bitsToNumber(packed.subarray(offset, offset + width));
    offset += width;
    if (lineNumber === 2) rhymeClass = rhymeClassOf(value);
    const index = lineNumber > 2 && lineNumber % 2 === 0 ? rhymeClass * LINES_PER_CLASS + value : value;
    indices.push(index);
  }

  const lines = await Promise.all(indices.map((index) => corpus.getLine(index)));
  return formatPoem(lines);
}

export async function decodePoem(
  poem: string,
  username: string,
  secret: string,
  corpus: Corpus,
): Promise<string> {
  const parsed = parsePoem(poem);
  const key = await deriveKey(username, secret, parsed.corpusId);
  return decodePoemWithKey(poem, key, corpus);
}

export async function decodePoemWithKey(
  poem: string,
  key: Uint8Array,
  corpus: Corpus,
): Promise<string> {
  const parsed = parsePoem(poem);
  await corpus.ensureReady();
  const indices: number[] = [];
  for (let line = 0; line < parsed.verseLines.length; line += 1) {
    let index: number | undefined;
    try {
      index = await corpus.getIndex(parsed.verseLines[line]!);
    } catch (error) {
      if (error instanceof AmbiguousCorpusLineError) {
        throw new CodecError("ambiguous-line", `第 ${line + 1} 句归一化后对应多条语料，请粘贴原句。`);
      }
      throw error;
    }
    if (index === undefined) {
      throw new CodecError("line-not-found", `第 ${line + 1} 句不在 ${corpus.id} 语料中。`);
    }
    indices.push(index);
  }

  const rhymeClass = indices.length >= 2 ? rhymeClassOf(indices[1]!) : 0;
  if (rhymeClass >= RHYME_CLASS_COUNT) throw new CodecError("rhyme-mismatch", "韵部编号无效。");
  const bitParts: Uint8Array[] = [];
  for (let line = 0; line < indices.length; line += 1) {
    const lineNumber = line + 1;
    const index = indices[line]!;
    if (lineNumber > 2 && lineNumber % 2 === 0) {
      if (rhymeClassOf(index) !== rhymeClass) {
        throw new CodecError("rhyme-mismatch", `第 ${lineNumber} 句没有押同一韵。`);
      }
      bitParts.push(numberToBits(withinClassIndexOf(index), WITHIN_CLASS_BITS));
    } else {
      bitParts.push(numberToBits(index, 21));
    }
  }

  const ciphertext = unpackCiphertext(concatBits(...bitParts), indices.length);
  const plaintext = await decryptWideBlock(ciphertext, key);
  try {
    return fatalUtf8Decoder.decode(plaintext);
  } catch {
    throw new CodecError("wrong-key", "这个用户名和暗语打不开这首诗帖。");
  }
}
